import { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, Navigate } from 'react-router-dom';
import BrandLogo from '../components/BrandLogo';
import OrganizationSwitcher from '../components/OrganizationSwitcher';
import { dashboardAPI } from '../services/api';
import { StudentStats, RecruiterStats, AdminStats } from '../components/Dashboard';
import TrainerBatchWorkspace from '../components/Dashboard/TrainerBatchWorkspace';
import usePermissions from '../hooks/usePermissions';
import '../styles/staff-dashboard.css';

const Dashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [joinDate, setJoinDate] = useState('');

  const token = user?.token;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const res = await dashboardAPI.getStats();
        setStats(res.data.stats || res.data || {});
      } catch (err) {
        console.error('Dashboard stats error:', err);
        setStats({});
      }
    };

    const getJoinDate = () => {
      const date = user?.createdAt || user?.user?.createdAt;
      if (date) {
        setJoinDate(
          new Date(date).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        );
      }
    };

    if (token) {
      fetchDashboardStats();
      getJoinDate();
    }
  }, [token, user?.role, user?.user?.role, user?.createdAt, user?.user?.createdAt]);

  const name =
    user?.name ||
    user?.user?.name ||
    `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
  const email = user?.email || user?.user?.email || '';
  const role = user?.role || user?.user?.role || '';
  const accessLevel = user?.accessLevelName || user?.user?.accessLevelName || '';
  const { navItems } = usePermissions();
  const showStaffNav = role === 'admin' || role === 'superadmin' || navItems.length > 0;

  if (role === 'student') {
    return <Navigate to="/student/dashboard" replace />;
  }

  return (
    <div className="staff-home">
      <div className="staff-home-inner">
        <header className="staff-topbar">
          <div className="staff-topbar-brand">
            <BrandLogo size="sm" />
            <div className="min-w-0">
              <h1 className="staff-topbar-title">VLSI Student Management System</h1>
              <p className="staff-topbar-sub">VLSI Expert Pvt. Ltd.</p>
            </div>
          </div>
          <div className="staff-topbar-actions">
            <OrganizationSwitcher />
            <button type="button" className="staff-logout" onClick={handleLogout}>
              Sign out
            </button>
          </div>
        </header>

        <section className="staff-welcome">
          <h2>Welcome{name ? `, ${name.split(' ')[0]}` : ''}</h2>
          <p>
            {showStaffNav
              ? 'Choose a workspace below. Access is based on your role and assigned permissions.'
              : 'Your account overview and available tools.'}
          </p>
        </section>

        <dl className="staff-profile-strip">
          <div>
            <dt>Name</dt>
            <dd>{name || '—'}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>{email || '—'}</dd>
          </div>
          <div>
            <dt>Role</dt>
            <dd>
              {role || '—'}
              {accessLevel ? ` · ${accessLevel}` : ''}
            </dd>
          </div>
          <div>
            <dt>Joined</dt>
            <dd>{joinDate || '—'}</dd>
          </div>
        </dl>

        {role === 'student' && <StudentStats stats={stats} />}
        {role === 'trainer' && <TrainerBatchWorkspace stats={stats} />}
        {role === 'recruiter' && !showStaffNav && (
          <div className="staff-section">
            <RecruiterStats stats={stats} />
          </div>
        )}
        {showStaffNav && (
          <AdminStats stats={stats} showOrgMetrics={role === 'admin'} />
        )}
      </div>
    </div>
  );
};

export default Dashboard;
